import React, { useEffect, useMemo } from 'react';
import {
  StyledManageDealerAvailabilityContainer,
  StyledManageDealerAvailabilityTitleContainer,
  StyledAvailabilityInfoContainer,
  StyledDayScheduleItem,
  StyledDayLabel,
  StyledTimeLabel,
  StyledClosedLabel,
  StyledManageDealerAvailabilityContentContainer,
} from './styles';
import { Select, Spinner } from '@volkswagen-onehub/components-core';
import {
  processDealerHours,
  getDepartmentSchedule,
  formatTimeRange,
  ProcessedDealerHours,
  ProcessedDepartment,
} from '../../../../shared/utils/dealer-hours-processor';
import { useMyVwContext } from '../../../../shared/hooks/use-my-vw-context';
import { useDealer } from '../../../../app/providers/DealerContext';
import { useFeatureServices } from '../../../../shared/hooks/use-feature-services';

interface ManageDealerAvailabilityProps {
  sundayFirst?: boolean;
}

const ManageDealerAvailability: React.FC<ManageDealerAvailabilityProps> = ({
  sundayFirst = false,
}) => {
  const {
    isLoadingPreferredDealer,
    userPreferredDealer,
    selectedDepartmentCode,
    setSelectedDepartmentCode,
  } = useDealer();
  const { translationHandler } = useMyVwContext();
  const { 'locale-service': localeService } = useFeatureServices();

  const isCanada = localeService?.countryCode === 'CA';
  const isCanadaFrench = isCanada && localeService?.language === 'fr';
  const locale = isCanadaFrench ? 'fr' : 'en';

  const processedDealerData: ProcessedDealerHours | null = useMemo(() => {
    return processDealerHours(userPreferredDealer, sundayFirst, locale);
  }, [userPreferredDealer, sundayFirst, locale]);

  const selectedDepartmentSchedule: ProcessedDepartment | null = useMemo(() => {
    return getDepartmentSchedule(processedDealerData, selectedDepartmentCode);
  }, [processedDealerData, selectedDepartmentCode]);

  // Auto-select first department if none selected and departments are available
  useEffect(() => {
    if (
      processedDealerData &&
      processedDealerData?.availableDepartments?.length > 0 &&
      !selectedDepartmentCode
    ) {
      setSelectedDepartmentCode(
        processedDealerData.availableDepartments[0].code,
      );
    }
  }, [processedDealerData, selectedDepartmentCode, setSelectedDepartmentCode]);

  const handleDepartmentChange = (
    e: React.SyntheticEvent<HTMLSelectElement, Event>,
  ) => {
    const target = e.target as HTMLSelectElement;
    setSelectedDepartmentCode(target.value);
  };

  if (isLoadingPreferredDealer) {
    return (
      <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
        <Spinner />
      </div>
    );
  }

  if (!processedDealerData) {
    return (
      <StyledManageDealerAvailabilityContainer>
        <StyledManageDealerAvailabilityTitleContainer>
          {translationHandler('dealerManager.hours.title')}
        </StyledManageDealerAvailabilityTitleContainer>
        <div>{translationHandler('dealerManager.hours.noData')}</div>
      </StyledManageDealerAvailabilityContainer>
    );
  }

  return (
    <StyledManageDealerAvailabilityContainer>
      <StyledManageDealerAvailabilityTitleContainer>
        {translationHandler('dealerManager.hours.title')}
      </StyledManageDealerAvailabilityTitleContainer>

      <StyledManageDealerAvailabilityContentContainer>
        {!isCanada && (
          <Select
            value={selectedDepartmentCode}
            onChange={handleDepartmentChange}
            aria-label={translationHandler('dealerManager.hours.title')}
          >
            {processedDealerData.availableDepartments?.map((department) => (
              <option key={department.code} value={department.code}>
                {translationHandler(
                  'dealerManager.hours.departmentHours',
                ).replace('{department}', department.description)}
              </option>
            ))}
          </Select>
        )}

        <StyledAvailabilityInfoContainer>
          {selectedDepartmentSchedule?.days?.map((daySchedule, index) => (
            <StyledDayScheduleItem key={index}>
              <StyledDayLabel>{daySchedule.day}</StyledDayLabel>

              {daySchedule.closedIndicator ? (
                <StyledClosedLabel>
                  {translationHandler('dealerManager.hours.closed')}
                </StyledClosedLabel>
              ) : (
                <StyledTimeLabel>
                  {formatTimeRange(
                    daySchedule.openTime,
                    daySchedule.closeTime,
                    daySchedule.closedIndicator,
                    locale,
                  )}
                </StyledTimeLabel>
              )}
            </StyledDayScheduleItem>
          ))}
        </StyledAvailabilityInfoContainer>
      </StyledManageDealerAvailabilityContentContainer>
    </StyledManageDealerAvailabilityContainer>
  );
};

export default ManageDealerAvailability;
